import { getAccessServiceDescription } from 'app/domain/AccessServiceTypes';
import CONTENTO_ACCESSIBILITIES from 'app/domain/ContentoAccessibilities';
import Link from 'next/link';
import React, { useState } from 'react';
import PlayBadge from '../../../../ui-components/PlayBadge/PlayBadge';
import { SVG_ICONS } from '../../../../ui-components/SvgIcon/SvgIcon';
import { SIZE } from '../../../../ui-components/typography/PlayText/PlayText';
import { VisuallyHiddenStyle } from '../../../../ui-components/utils.styles';
import { contentoImageUrl } from '../../../../utils/svtimage';
import OpenModal from '../../../VideoModal/OpenModal.tsx';
import {
    AccessibilitySvgIcon,
    AccessibilityVariants,
    AssociatedMoreInfoLink,
    AssociatedProgressBar,
    AssoiciatedContentImage,
    Content,
    ContentDescription,
    ContentHeader,
    ContentLink,
    ContentSubheader,
    ExpandButton,
    ImageContainer,
    ImageLink,
    Information,
    MobileDescription,
    StyledPlayIcon,
    UpcomingOverlay,
    UpcomingOverlayText,
} from './AssociatedContent.styles';
import { upcomingDateFormatted } from './util';

const AssociatedContentItem = ({ content, type, selectedVideoId, progress, trackClick, index }) => {
    const imageSrc = contentoImageUrl(content.images.wide, 'large', 600);
    const isUpcoming = type === 'upcoming';
    const [expanded, setExpanded] = useState(false);

    const accessibilities = content.item.accessibilities.filter((accessibility) =>
        [CONTENTO_ACCESSIBILITIES.AUDIO_DESCRIBED, CONTENTO_ACCESSIBILITIES.SIGN_INTERPRETED].includes(
            accessibility
        )
    );

    const progressPercentage = progress ? progress[content.item.svtId] : null;
    const accessibleLabel = `Spela ${content.heading}`;

    const getIcon = (accessibility) => {
        if (accessibility === CONTENTO_ACCESSIBILITIES.SIGN_INTERPRETED) {
            return SVG_ICONS.SIGN_LANGUAGE;
        }

        if (accessibility === CONTENTO_ACCESSIBILITIES.AUDIO_DESCRIBED) {
            return SVG_ICONS.SIGHT;
        }

        return '';
    };

    return (
        <Content selected={content.item.videoSvtId === selectedVideoId} className={`lp_pos${index + 1}`}>
            <ImageContainer>
                {isUpcoming ? (
                    <>
                        <ImageLink as="div" isUpcoming aria-label="">
                            <AssoiciatedContentImage
                                src={imageSrc}
                                faded
                                blurred={content.item.restrictions.onlyAvailableInSweden}
                                onlyAvailableInSweden={content.item.restrictions.onlyAvailableInSweden}
                            />
                            <UpcomingOverlay
                                onlyAvailableInSweden={content.item.restrictions.onlyAvailableInSweden}
                                aria-hidden={true}
                            >
                                <UpcomingOverlayText bold>
                                    {upcomingDateFormatted(content.item.validFromFormatted)[0]}
                                </UpcomingOverlayText>
                                <UpcomingOverlayText>
                                    {upcomingDateFormatted(content.item.validFromFormatted)[1]}
                                </UpcomingOverlayText>
                            </UpcomingOverlay>
                        </ImageLink>
                    </>
                ) : (
                    <OpenModal videoId={content.item.videoSvtId} videoUrl={content.item.urls.svtplay}>
                        {(onClick) => (
                            <ImageLink
                                tabIndex={-1}
                                isUpcoming={false}
                                aria-label={accessibleLabel}
                                onClick={(e) => {
                                    onClick();
                                    trackClick(e, true);
                                }}
                                data-rt="associated-image-link"
                            >
                                <StyledPlayIcon icon={SVG_ICONS.PLAY} width={50} />
                                <AssoiciatedContentImage
                                    src={imageSrc}
                                    blurred={content.item.restrictions.onlyAvailableInSweden}
                                    onlyAvailableInSweden={content.item.restrictions.onlyAvailableInSweden}
                                >
                                    {content.badge && (
                                        <PlayBadge
                                            text={content?.badge?.text}
                                            theme={content?.badge?.type}
                                            accessibilityText={content?.badge?.altText}
                                            overlay
                                        />
                                    )}
                                </AssoiciatedContentImage>
                                {progressPercentage ? (
                                    <AssociatedProgressBar progress={progressPercentage} selected />
                                ) : null}
                            </ImageLink>
                        )}
                    </OpenModal>
                )}
            </ImageContainer>
            <Information isUpcoming={isUpcoming}>
                <ContentHeader as="h3">
                    {isUpcoming ? (
                        <>
                            <span aria-hidden="true">{content.heading}</span>
                            <VisuallyHiddenStyle as="span">
                                {content.item.nameRaw
                                    ? `${content.item.positionInSeason}. ${content.item.nameRaw}`
                                    : content.heading}
                            </VisuallyHiddenStyle>
                        </>
                    ) : (
                        <Link
                            href={{
                                pathname: content.item.urls.svtplay,
                                query: {
                                    info: 'visa',
                                },
                            }}
                            passHref
                        >
                            <ContentLink
                                aria-label={accessibleLabel}
                                onClick={(e) => trackClick(e, false)}
                                data-rt="associated-header-link"
                            >
                                {content.heading}
                            </ContentLink>
                        </Link>
                    )}
                </ContentHeader>
                <ContentSubheader size={SIZE.SMALL}>
                    <VisuallyHiddenStyle as="span">
                        {`${isUpcoming ? 'Publiceras' : 'Publicerades'}: ${content.item.validFromFormatted}`}
                    </VisuallyHiddenStyle>
                    <span aria-hidden={true}>{content.subHeading}</span>
                    {accessibilities.length > 0 && (
                        <AccessibilityVariants>
                            <VisuallyHiddenStyle as="span">
                                {`Finns som ${accessibilities
                                    .map((accessibility) => getAccessServiceDescription(accessibility))
                                    .join(' och ')}`}
                            </VisuallyHiddenStyle>
                            {accessibilities.map((accessibility) => (
                                <AccessibilitySvgIcon
                                    key={`accessibility-${accessibility}`}
                                    icon={getIcon(accessibility)}
                                    accessibilityText="test"
                                    width={18}
                                />
                            ))}
                        </AccessibilityVariants>
                    )}
                </ContentSubheader>
                <ContentDescription size={SIZE.LARGE} expanded={expanded} isUpcoming={isUpcoming}>
                    {content.description}
                    {!isUpcoming && (
                        <Link
                            href={{
                                pathname: content.item.urls.svtplay,
                                query: {
                                    info: 'visa',
                                },
                            }}
                            passHref
                        >
                            <AssociatedMoreInfoLink
                                data-rt="associated-more-info-link"
                                onClick={(e) => trackClick(e, false)}
                            >
                                Mer om programmet
                            </AssociatedMoreInfoLink>
                        </Link>
                    )}
                </ContentDescription>
            </Information>
            <ExpandButton
                expanded={expanded}
                isUpcoming={isUpcoming}
                onClick={() => setExpanded(!expanded)}
                aria-label={`Visa beskrivning för ${content.heading}`}
            />

            <MobileDescription expanded={expanded} size={SIZE.LARGE}>
                {content.description}
                {!isUpcoming && (
                    <Link
                        href={{
                            pathname: content.item.urls.svtplay,
                            query: {
                                info: 'visa',
                            },
                        }}
                        passHref
                    >
                        <AssociatedMoreInfoLink onClick={(e) => trackClick(e, false)}>
                            Mer om programmet
                        </AssociatedMoreInfoLink>
                    </Link>
                )}
            </MobileDescription>
        </Content>
    );
};

export default AssociatedContentItem;
